import * as React from 'react'

import CloseIcon from '@mui/icons-material/Close'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'

import type { ILeadMissingFieldDTO } from '@user/store/lead/typings'

import { LeadCompanyRequiredFieldsContent } from './LeadCompanyRequiredFieldsContent'

interface Props {
  open: boolean
  leadId: number
  missingFields: ILeadMissingFieldDTO[]
  onClose: () => void
  /** Called after a successful save. */
  onSaved: () => void
}

/**
 * "Update Required Fields" modal for an auto-reviewed lead missing company-required fields.
 * Mounted only while open so the form resets on close.
 */
export function LeadCompanyRequiredFieldsDialog(props: Props) {
  const { open, leadId, missingFields, onClose, onSaved } = props

  if (!open) return null

  return (
    <Dialog
      open
      onClose={onClose}
      PaperProps={{ sx: { width: 530 } }}
      data-test-id="lead-company-required-fields-dialog"
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #e2e5ea',
          mb: '20px',
        }}
      >
        <Typography variant={'subtitle3'}>Update Required Fields</Typography>

        <IconButton
          onClick={onClose}
          size={'small'}
          sx={{
            cursor: 'pointer',
          }}
          data-test-id="lead-company-required-fields-close"
        >
          <CloseIcon fontSize={'small'} />
        </IconButton>
      </DialogTitle>

      <Typography variant="body2" px={'24px'}>
        This lead is missing company-required fields. Complete them below to
        allow it to be converted or close/won.
      </Typography>

      <LeadCompanyRequiredFieldsContent
        leadId={leadId}
        missingFields={missingFields}
        onClose={onClose}
        onSaved={onSaved}
      />
    </Dialog>
  )
}

LeadCompanyRequiredFieldsDialog.displayName = 'LeadCompanyRequiredFieldsDialog'
